import { EditOutlined, PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Form, Input, InputNumber, Row, Space, Switch, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { SaveActionButton } from "../../shared/components/SaveActionButton";

type TransportServiceTaxProfileRow = {
  id: number;
  serviceCode: string;
  sacCode: string;
  isActive: boolean;
};

type SacTaxProfileRow = {
  id: number;
  sacCode: string;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  isActive: boolean;
};

const TransportServiceProfilesSection = () => {
  const [rows, setRows] = useState<TransportServiceTaxProfileRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TransportServiceTaxProfileRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await transportApi.listTransportServiceTaxProfiles();
      setRows(data as TransportServiceTaxProfileRow[]);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to load transport service tax profiles"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, serviceCode: "freight", sacCode: "996511" });
    setDrawerOpen(true);
  };

  const openEdit = (row: TransportServiceTaxProfileRow) => {
    setEditing(row);
    form.setFieldsValue(row);
    setDrawerOpen(true);
  };

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        serviceCode: String(values.serviceCode).trim().toLowerCase(),
        sacCode: String(values.sacCode).trim().toUpperCase(),
        isActive: values.isActive ?? true
      };

      if (editing) {
        await transportApi.updateTransportServiceTaxProfile(editing.id, payload);
        message.success("Transport service tax profile updated");
      } else {
        await transportApi.createTransportServiceTaxProfile(payload);
        message.success("Transport service tax profile created");
      }

      setDrawerOpen(false);
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to save transport service tax profile"));
    } finally {
      setSaving(false);
    }
  };

  const seedDefaults = async () => {
    try {
      const result = await transportApi.seedTransportServiceTaxProfiles() as { message?: string };
      message.success(result?.message ?? "Transport service tax defaults seeded");
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to seed transport service tax profiles"));
    }
  };

  const columns: ColumnsType<TransportServiceTaxProfileRow> = [
    {
      title: "SN",
      width: 70,
      render: (_, __, index) => index + 1
    },
    {
      title: "Service Code",
      dataIndex: "serviceCode",
      render: (value: string) => <strong>{value}</strong>
    },
    {
      title: "SAC Code",
      dataIndex: "sacCode",
      width: 120
    },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 100,
      render: (value: boolean) => <Tag color={value ? "green" : "default"}>{value ? "Active" : "Inactive"}</Tag>
    },
    {
      title: "",
      width: 80,
      render: (_, row) => <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
    }
  ];

  return (
    <Card
      title="Transport Service → SAC Mapping"
      extra={
        <Space>
          <Button icon={<ThunderboltOutlined />} onClick={() => void seedDefaults()}>Seed Defaults</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Mapping</Button>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="These mappings decide which SAC code is used for transport service categories."
        description="The consignment GST calculation currently depends on the 'freight' mapping. Defaults are provided for common charge types and can be edited here."
      />

      <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={false} />

      <StaticDrawer
        title={editing ? "Edit Transport Service Mapping" : "Add Transport Service Mapping"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button onClick={() => setDrawerOpen(false)}>Cancel</Button><SaveActionButton saving={saving} idleText={editing ? "Update" : "Save"} savingText={editing ? "Updating Mapping..." : "Saving Mapping..."} onClick={() => void onSave()} /></div>}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ isActive: true }}>
          <Form.Item
            name="serviceCode"
            label="Service Code"
            rules={[{ required: true, message: "Service code is required" }]}
            extra="Use lowercase charge types such as freight, hamali, loading, unloading, detention, or other."
          >
            <Input placeholder="freight" />
          </Form.Item>
          <Form.Item
            name="sacCode"
            label="SAC Code"
            rules={[{ required: true, message: "SAC code is required" }]}
          >
            <Input placeholder="996511" />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </StaticDrawer>
    </Card>
  );
};

const SacTaxProfilesSection = () => {
  const [rows, setRows] = useState<SacTaxProfileRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<SacTaxProfileRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await transportApi.listSacTaxProfiles();
      setRows(data as SacTaxProfileRow[]);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to load SAC tax profiles"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, sacCode: "996511", cgstRate: 2.5, sgstRate: 2.5, igstRate: 5 });
    setDrawerOpen(true);
  };

  const openEdit = (row: SacTaxProfileRow) => {
    setEditing(row);
    form.setFieldsValue(row);
    setDrawerOpen(true);
  };

  const onSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        sacCode: String(values.sacCode).trim().toUpperCase(),
        cgstRate: Number(values.cgstRate ?? 0),
        sgstRate: Number(values.sgstRate ?? 0),
        igstRate: Number(values.igstRate ?? 0),
        isActive: values.isActive ?? true
      };

      if (editing) {
        await transportApi.updateSacTaxProfile(editing.id, payload);
        message.success("SAC tax profile updated");
      } else {
        await transportApi.createSacTaxProfile(payload);
        message.success("SAC tax profile created");
      }

      setDrawerOpen(false);
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to save SAC tax profile"));
    } finally {
      setSaving(false);
    }
  };

  const seedDefaults = async () => {
    try {
      const result = await transportApi.seedSacTaxProfiles() as { message?: string };
      message.success(result?.message ?? "SAC defaults seeded");
      await load();
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to seed SAC tax profiles"));
    }
  };

  const columns: ColumnsType<SacTaxProfileRow> = [
    {
      title: "SN",
      width: 70,
      render: (_, __, index) => index + 1
    },
    {
      title: "SAC Code",
      dataIndex: "sacCode",
      width: 120,
      render: (value: string) => <strong>{value}</strong>
    },
    { title: "CGST %", dataIndex: "cgstRate", width: 100 },
    { title: "SGST %", dataIndex: "sgstRate", width: 100 },
    { title: "IGST %", dataIndex: "igstRate", width: 100 },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 100,
      render: (value: boolean) => <Tag color={value ? "green" : "default"}>{value ? "Active" : "Inactive"}</Tag>
    },
    {
      title: "",
      width: 80,
      render: (_, row) => <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
    }
  ];

  return (
    <Card
      title="SAC GST Rates"
      extra={
        <Space>
          <Button icon={<ThunderboltOutlined />} onClick={() => void seedDefaults()}>Seed Defaults</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add SAC Rate</Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="These GST rates are referenced by the transport service mappings above."
        description="Defaults are seeded for common transport and logistics SAC codes, and you can adjust or add more here as your tax setup evolves."
      />

      <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={{ pageSize: 10, showSizeChanger: false }} />

      <StaticDrawer
        title={editing ? "Edit SAC Tax Profile" : "Add SAC Tax Profile"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        extra={<SaveActionButton saving={saving} idleText={editing ? "Update" : "Save"} savingText={editing ? "Updating SAC Profile..." : "Saving SAC Profile..."} onClick={() => void onSave()} />}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ isActive: true, cgstRate: 0, sgstRate: 0, igstRate: 0 }}>
          <Form.Item
            name="sacCode"
            label="SAC Code"
            rules={[{ required: true, message: "SAC code is required" }]}
          >
            <Input placeholder="996511" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="cgstRate" label="CGST %" rules={[{ required: true, message: "Required" }]}>
                <InputNumber min={0} max={100} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sgstRate" label="SGST %" rules={[{ required: true, message: "Required" }]}>
                <InputNumber min={0} max={100} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="igstRate" label="IGST %" rules={[{ required: true, message: "Required" }]}>
                <InputNumber min={0} max={100} step={0.01} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </StaticDrawer>
    </Card>
  );
};

const TransportTaxProfilesTab = () => (
  <>
    <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
      Configure the GST mapping used by freight calculation. Transport service mappings point to SAC codes, and SAC profiles hold the actual CGST/SGST/IGST rates.
    </Typography.Paragraph>
    <TransportServiceProfilesSection />
    <SacTaxProfilesSection />
  </>
);

export default TransportTaxProfilesTab;
